import { API_BASE_URL } from '../config/env';
import type {
  AuthTokens,
  AuthUser,
  ForgotPasswordPayload,
  LoginCredentials,
  RegisterPayload,
  ResendVerificationPayload,
  ResetPasswordPayload,
  SignupResult,
  VerifyEmailPayload,
} from '../types/auth';
import { formatApiErrorMessage } from '../utils/apiErrorMessage';
import { AuthApiError } from './AuthApiError';
import { refreshSession, storeTokensFromAuthResponse } from './authSessionRefresh';
import { stopAuthTokenRefresh } from './authTokenRefresh';
import {
  clearStoredTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
} from './authStorage';
import { parseAuthTokensPayload } from './parseTokens';

export { AuthApiError } from './AuthApiError';
export { refreshSession } from './authSessionRefresh';

const AUTH_PATHS_NO_REFRESH = [
  '/auth/refresh',
  '/auth/login',
  '/auth/signup',
  '/auth/register',
  '/auth/logout',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/forgot-password',
  '/auth/reset-password',
];

function shouldRetryWithRefresh(
  path: string,
  status: number,
  sentAccessToken: boolean,
  retried: boolean,
): boolean {
  if (retried || status !== 401 || !sentAccessToken) return false;
  if (!getStoredRefreshToken()) return false;
  return !AUTH_PATHS_NO_REFRESH.some((p) => path.startsWith(p));
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function signupRequiresVerification(body: unknown): boolean {
  if (!body || typeof body !== 'object') return false;
  const o = body as Record<string, unknown>;
  return (
    o.requiresEmailVerification === true ||
    o.emailVerificationRequired === true ||
    o.requiresVerification === true ||
    o.verificationRequired === true
  );
}

function signupMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const msg = (body as Record<string, unknown>).message;
  return typeof msg === 'string' ? msg : undefined;
}

/**
 * Authenticated API calls. On 401, refreshes once (deduplicated), rotates tokens, retries.
 */
export async function apiRequest<T>(
  path: string,
  init: RequestInit & { accessToken?: string | null; _retriedAfterRefresh?: boolean } = {},
): Promise<T> {
  const { accessToken, headers: initHeaders, _retriedAfterRefresh, ...rest } = init;
  const headers = new Headers(initHeaders);
  if (!headers.has('Content-Type') && rest.body != null) {
    headers.set('Content-Type', 'application/json');
  }
  const explicitToken = accessToken !== undefined ? accessToken : getStoredAccessToken();
  const sentAccessToken = Boolean(explicitToken);
  if (explicitToken) headers.set('Authorization', `Bearer ${explicitToken}`);

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers,
    credentials: 'same-origin',
  });
  const body = await readJson(res);

  if (!res.ok) {
    if (
      shouldRetryWithRefresh(path, res.status, sentAccessToken, Boolean(_retriedAfterRefresh))
    ) {
      try {
        await refreshSession();
        return apiRequest<T>(path, { ...init, _retriedAfterRefresh: true });
      } catch {
        clearStoredTokens();
        stopAuthTokenRefresh();
      }
    }

    const msg = formatApiErrorMessage(body, res.statusText) || `Request failed (${res.status})`;
    const code =
      typeof body === 'object' && body !== null && 'code' in body
        ? String((body as { code: unknown }).code)
        : undefined;
    throw new AuthApiError(msg, res.status, body, code);
  }

  return body as T;
}

/** Sign up; may return tokens or require email verification first. */
export async function authSignup(dto: RegisterPayload): Promise<SignupResult> {
  const data = await apiRequest<unknown>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(dto),
    accessToken: null,
  });
  const tokens = parseAuthTokensPayload(data);
  const email = dto.email.trim();

  if (!tokens || signupRequiresVerification(data)) {
    clearStoredTokens();
    stopAuthTokenRefresh();
    return {
      kind: 'verification_required',
      message:
        signupMessage(data) ||
        'Account created. Check your email and click the verification link before signing in.',
      email,
    };
  }

  storeTokensFromAuthResponse(data, tokens);
  return { kind: 'session', tokens };
}

export async function authLogin(dto: LoginCredentials): Promise<AuthTokens> {
  const data = await apiRequest<unknown>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(dto),
    accessToken: null,
  });
  const tokens = parseAuthTokensPayload(data);
  if (!tokens) throw new AuthApiError('Invalid login response: missing tokens', 500, data);
  storeTokensFromAuthResponse(data, tokens);
  return tokens;
}

function unwrapData<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in body) {
    const d = (body as { data: unknown }).data;
    if (d !== undefined && d !== null) return d as T;
  }
  return body as T;
}

export async function authMe(): Promise<AuthUser> {
  const body = await apiRequest<unknown>('/auth/me', { method: 'GET' });
  return unwrapData<AuthUser>(body);
}

export async function authVerifyEmail(payload: VerifyEmailPayload): Promise<AuthTokens> {
  const data = await apiRequest<unknown>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify(payload),
    accessToken: null,
  });
  const tokens = parseAuthTokensPayload(data);
  if (!tokens) {
    throw new AuthApiError('Invalid verification response: missing tokens', 500, data);
  }
  storeTokensFromAuthResponse(data, tokens);
  return tokens;
}

export async function authResendVerification(payload: ResendVerificationPayload): Promise<void> {
  await apiRequest<unknown>('/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify(payload),
    accessToken: null,
  });
}

export async function authForgotPassword(payload: ForgotPasswordPayload): Promise<void> {
  await apiRequest<unknown>('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(payload),
    accessToken: null,
  });
}

export async function authResetPassword(payload: ResetPasswordPayload): Promise<void> {
  await apiRequest<unknown>('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(payload),
    accessToken: null,
  });
}

/** Rotates tokens via deduplicated `POST /auth/refresh`. */
export async function authRefresh(): Promise<AuthTokens> {
  return refreshSession();
}

export async function authLogout(): Promise<void> {
  const rt = getStoredRefreshToken();
  if (rt) {
    try {
      await apiRequest<unknown>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: rt }),
        accessToken: null,
      });
    } catch {
      /* still clear local session */
    }
  }
  clearStoredTokens();
  stopAuthTokenRefresh();
}

export async function authLogoutAll(): Promise<void> {
  await apiRequest<unknown>('/auth/logout-all', { method: 'POST' });
  clearStoredTokens();
  stopAuthTokenRefresh();
}
